import { useRouter } from "next/navigation";
import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import useResetStateTravelerEdit from "@/app/common-hooks/use-reset-state-traveler-edit/user-reset-state-traveler-edit";

import {
  AvailbleOptionsType,
  CoverForPageContent,
  GroupedCmsDataType,
} from "../types/cover-for.type";
import { groupedCoverForCmsData } from "../utils/cover-for-utils";

// Extend the context type
const CoverForContext = createContext<{
  cmsData: CoverForPageContent;
  selectedAnswer: "DEFAULT" | AvailbleOptionsType;
  groupedCmsData: GroupedCmsDataType;
  isDrawerOpen: boolean;
  isFamilyModalOpen: boolean;
  isOpenUnsavedChangesModal: boolean;
  shouldTriggerToggleWrapper: boolean;
  scrollRefArray: {
    ref: React.RefObject<HTMLDivElement | null>;
    isToggleWrapper?: boolean;
  }[];
  toggleWrapperRef: React.RefObject<HTMLDivElement | null>;
  setShouldTriggerToggleWrapper: Dispatch<SetStateAction<boolean>>;
  setSelectedAnswer: Dispatch<SetStateAction<"DEFAULT" | AvailbleOptionsType>>;
  setIsDrawerOpen: Dispatch<SetStateAction<boolean>>;
  setIsFamilyModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsOpenUnsavedChangesModal: Dispatch<SetStateAction<boolean>>;
  handleUnSavedWarningMessageContinueBtn: () => void;
} | null>(null);

// Provider component
function CoverForProvider({
  children,
  initialValue,
}: Readonly<{
  children: ReactNode;
  initialValue: CoverForPageContent;
}>) {
  const [selectedAnswer, setSelectedAnswer] = useState<
    "DEFAULT" | AvailbleOptionsType
  >("DEFAULT");
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isFamilyModalOpen, setIsFamilyModalOpen] = useState<boolean>(false);
  const [isOpenUnsavedChangesModal, setIsOpenUnsavedChangesModal] =
    useState<boolean>(false);
  const router = useRouter();
  const { resetQuestionData } = useResetStateTravelerEdit();
  const [shouldTriggerToggleWrapper, setShouldTriggerToggleWrapper] =
    useState(false);

  const toggleWrapperRef = useRef<HTMLDivElement>(null);

  const groupedCmsData = useMemo(() => {
    return groupedCoverForCmsData(initialValue);
  }, [initialValue]);

  const handleUnSavedWarningMessageContinueBtn = () => {
    setIsOpenUnsavedChangesModal(false);
    resetQuestionData();
    router.push("/");
  };

  const scrollRefArray = useMemo(() => {
    return [
      {
        ref: toggleWrapperRef,
        isToggleWrapper: true,
      },
    ];
  }, [toggleWrapperRef]);

  const contextValue = useMemo(
    () => ({
      cmsData: initialValue,
      selectedAnswer,
      groupedCmsData,
      isDrawerOpen,
      isFamilyModalOpen,
      isOpenUnsavedChangesModal,
      scrollRefArray,
      toggleWrapperRef,
      shouldTriggerToggleWrapper,
      setShouldTriggerToggleWrapper,
      handleUnSavedWarningMessageContinueBtn,
      setIsOpenUnsavedChangesModal,
      setIsFamilyModalOpen,
      setIsDrawerOpen,
      setSelectedAnswer,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      initialValue,
      selectedAnswer,
      isDrawerOpen,
      groupedCmsData,
      isFamilyModalOpen,
      isOpenUnsavedChangesModal,
      toggleWrapperRef,
      scrollRefArray,
    ]
  );

  return (
    <CoverForContext.Provider value={contextValue}>
      {children}
    </CoverForContext.Provider>
  );
}

// Custom hook to use the context
export const useCoverForContext = () => {
  const context = useContext(CoverForContext);
  if (!context) {
    throw new Error(
      "useCoverForContext must be used within a CoverForMagnoliaProvider"
    );
  }
  return context;
};

export default CoverForProvider;
